import React, { useMemo } from "react";
import type { StatsNode } from "../../../types/graphStatsTypes";
import { STATS_COLORS, getSuccessRateColor } from "../../../types/graphStatsTypes";

interface StatsNodeSceneProps {
  node: StatsNode;
  dimmed?: boolean;
  onClick?: (node: StatsNode) => void;
  onMouseEnter?: (node: StatsNode, e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
}

// Static dimensions
const WIDTH = 150;
const HEIGHT = 150;

const buildTSpans = (title: string, isStartScene: boolean): JSX.Element | JSX.Element[] | null => {
  const elements = title.split(/\s+/, 2);
  // Start scene (triangle) has less space for text
  const maxLength = isStartScene ? 6 : 10;
  const truncateLength = isStartScene ? 4 : 7;

  switch (elements.length) {
    case 0:
      return null;
    case 1: {
      let text = title;
      if (text.length > maxLength) text = text.substring(0, truncateLength) + "...";
      return (
        <tspan x="50%" y="50%" key="title" alignmentBaseline="middle">
          {text.toLocaleUpperCase()}
        </tspan>
      );
    }
    default: {
      let [topTitle, bottomTitle] = elements;
      if (topTitle.length > maxLength) topTitle = topTitle.substring(0, truncateLength) + "...";
      if (bottomTitle.length > maxLength)
        bottomTitle = bottomTitle.substring(0, truncateLength) + "...";
      return [
        <tspan key="top-title" x="50%" y="45%">
          {topTitle.toLocaleUpperCase()}
        </tspan>,
        <tspan key="bottom-title" x="50%" y="55%">
          {bottomTitle.toLocaleUpperCase()}
        </tspan>,
      ];
    }
  }
};

const StatsNodeScene: React.FC<StatsNodeSceneProps> = ({
  node,
  dimmed = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const tspans = useMemo(
    () => buildTSpans(node.name, node.isStartScene || false),
    [node.name, node.isStartScene],
  );

  // Not visited is ONLY when explicitly set to false (session view)
  const isNotVisited = node.visited === false;
  const hasStats = node.stats?.successRate != null;

  // Calculate opacity: dimmed takes priority
  const nodeOpacity = dimmed ? 0.15 : 1;

  // Determine fill color based on success rate:
  // - NOT_VISITED (gray): only in session view when node.visited === false
  // - Has stats: color based on success rate (green >= 80%, yellow 50-80%, red < 50%)
  // - No stats: purple (NON_SCORED) - scene has no scored interactions inside
  let fillColor: string = STATS_COLORS.NON_SCORED;
  let strokeColor: string = STATS_COLORS.NON_SCORED;

  if (isNotVisited) {
    // Session view: not visited scene
    fillColor = STATS_COLORS.NOT_VISITED;
    strokeColor = STATS_COLORS.NOT_VISITED;
  } else if (hasStats && node.stats) {
    // Scene with stats - color based on average success rate of interactions inside
    fillColor = getSuccessRateColor(node.stats.successRate);
    strokeColor = getSuccessRateColor(node.stats.successRate);
  }

  const strokeWidth = 2;
  const textColor = isNotVisited ? "#9ca3af" : "#010038";

  const handleClick = () => {
    if (onClick) onClick(node);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (onMouseEnter) onMouseEnter(node, e);
  };

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox="0 0 100 120"
      x={node.x}
      y={node.y}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
      className={dimmed ? "cursor-default" : "cursor-pointer"}
      style={{ overflow: "visible" }}
    >
      <g fill="none" opacity={nodeOpacity}>
        {node.isStartScene ? (
          <polygon
            points="25,0 25,120 100,60"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={isNotVisited ? "8,4" : undefined}
            fill={fillColor}
          />
        ) : (
          <circle
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={isNotVisited ? "8,4" : undefined}
            fill={fillColor}
            cx="50%"
            cy="50%"
            r={39.5}
          />
        )}
        <text fontSize={13} letterSpacing={-0.336} fill={textColor} textAnchor="middle">
          {tspans}
        </text>
      </g>
    </svg>
  );
};

// Attach static dimensions for layout calculations
const StatsNodeSceneWithDimensions = Object.assign(StatsNodeScene, {
  WIDTH,
  HEIGHT,
});

export default StatsNodeSceneWithDimensions;
